import type { ReactNode } from 'react'
import { Button } from './ui/button'
import { AppIcon } from './ui/icon'
import { mdiClose } from '@mdi/js'
import { useTranslation } from 'react-i18next'

interface BulkActionsBarProps {
    /** Number of currently-selected items. */
    count: number
    /** Human-readable "{n} selected" label. */
    label: string
    /** Clears the current selection. */
    onClear: () => void
    /** Action buttons (Delete, Change type…, …). */
    children: ReactNode
    /** When true the bar shows a spinner and the caller disables its buttons. */
    busy?: boolean
    /** data-testid for the bar container. */
    'data-testid'?: string
}

/**
 * Generic sticky toolbar shown between a list page's filters and its table when
 * one or more rows are selected. Kept intentionally list-agnostic (count,
 * onClear, action buttons as children) so other list pages can reuse it.
 */
export function BulkActionsBar({
    count,
    label,
    onClear,
    children,
    busy = false,
    'data-testid': dataTestid,
}: BulkActionsBarProps) {
    const { t } = useTranslation('documents')

    if (count <= 0) return null

    return (
        <div
            data-testid={dataTestid}
            className="sticky top-0 z-10 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2"
        >
            <div className="flex items-center gap-1 text-sm font-medium text-foreground">
                {busy && (
                    <span
                        className="mr-1 inline-block h-4 w-4 motion-safe:animate-spin rounded-full border-2 border-muted-foreground/40 border-t-primary"
                        aria-hidden="true"
                    />
                )}
                <span data-testid="bulk-count">{label}</span>
            </div>

            <Button
                variant="ghost"
                size="icon"
                onClick={onClear}
                disabled={busy}
                aria-label={t('bulk.clear_selection')}
                title={t('bulk.clear_selection')}
                data-testid="bulk-clear-btn"
            >
                <AppIcon path={mdiClose} size="md" />
            </Button>

            <div className="ml-auto flex flex-wrap items-center gap-2">
                {children}
            </div>
        </div>
    )
}
